import React, { useEffect } from 'react';
import gql from 'graphql-tag';
import OffMarketContactAgentForm from '@zillow/off-market-contact-agent-form';
import { useQuery, useApolloClient } from '@apollo/client';
import { REQUIRED_PROPERTY_DIMENSIONS_FRAGMENTS } from '@zillow/universal-analytics-react';
import { Spinner } from '@zillow/constellation';
import styled from 'styled-components';
import ErrorMessage from './ErrorMessage';
import HomeInfo from './HomeInfo';
import ErrorWrapper from './ErrorWrapper';
import ResponsiveLightbox from './ResponsiveLightbox';
import OmpHomeWorthUpsell from './OmpHomeWorthUpsell';
import HomeDetailLightboxContent from './HomeDetailLightboxContent';

function getQuery() {
    return gql`
        query HowMuchIsMyHomeWorthReviewQuery($zpid: ID!) {
            property(zpid: $zpid) {
                streetAddress
                city
                state
                zipcode
                bedrooms
                bathrooms
                livingArea
                zestimate
                homeStatus
                photos(size: XL) {
                    url
                }
                ...OmpHomeWorthUpsell_property
                isConfirmedClaimedByCurrentSignedInUser
                isVerifiedClaimedByCurrentSignedInUser
                ...UARequiredPropertyDimensions_property
                ...ContactAgentForm_property
                ...HomeInfo_property
            }
            viewer {
                ...ContactAgentForm_viewer
            }
            abTests {
                ...UARequiredPropertyDimensions_abTests
                ...ContactAgentForm_abTests
            }
        }
        ${OmpHomeWorthUpsell.fragments.property}
        ${REQUIRED_PROPERTY_DIMENSIONS_FRAGMENTS.property}
        ${REQUIRED_PROPERTY_DIMENSIONS_FRAGMENTS.abTests}
        ${OffMarketContactAgentForm.fragments.property}
        ${OffMarketContactAgentForm.fragments.viewer}
        ${OffMarketContactAgentForm.fragments.abTests}
        ${HomeInfo.fragments.property}
    `;
}

const StyledResponsiveLightbox = styled(ResponsiveLightbox)`
    align-items: center;
    height: 626px;
    display: flex;
    justify-content: center;
`;

export const StyledLoadingContent = ({ isLightboxBackgroundLocked }) => (
    <StyledResponsiveLightbox isLightboxBackgroundLocked={isLightboxBackgroundLocked}>
        <Spinner size="lg" data-testid="loading-status" />
    </StyledResponsiveLightbox>
);

export const StyledErrorContent = () => (
    <ErrorWrapper>
        <ErrorMessage shouldShowRefreshPagePrompt>
            Looks like we&apos;re unable to get information for your home&apos;s listing right now.
        </ErrorMessage>
    </ErrorWrapper>
);

interface HomeDetailLightboxProps {
    zpid;
    loggedIn?: boolean;
    setZpid;
    zatlasEndpoint: string;
    surfaceId: string;
    placementId: string;
    xConsumerUsername: string;
    isLightboxBackgroundLocked: boolean;
    setHmimhwAnalyticsData?: (data) => void;
    isCMS?: boolean;
}

const HomeDetailLightbox = (props: HomeDetailLightboxProps) => {
    const client = useApolloClient();
    const query = getQuery();
    // TODO: remove "client" from useQuery call and default to client provided by context provider
    // https://www.apollographql.com/docs/react/data/queries/#client
    const { zpid, setHmimhwAnalyticsData, isLightboxBackgroundLocked } = props;
    const { data, loading, error } = useQuery(query, { variables: { zpid }, client });

    useEffect(() => {
        if (typeof setHmimhwAnalyticsData === 'function' && !loading && !error && data) {
            setHmimhwAnalyticsData(data);
        }
    }, [setHmimhwAnalyticsData, loading, error, data]);
    if (loading) {
        return <StyledLoadingContent isLightboxBackgroundLocked={isLightboxBackgroundLocked} />;
    }
    if (error || !data?.property) {
        return <StyledErrorContent />;
    }
    return <HomeDetailLightboxContent {...props} {...data} />;
};

export default HomeDetailLightbox;
